import { OptionType } from '@evinced-private/ui-common';

import { ENVIRONMENT_LABEL_KEY, UNIT_ID_LABEL_KEY } from 'src/common/consts/platformConfigConsts';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

/**
 * Build a map of display names to original label keys
 * Used to normalize labels that use display names back to their original keys
 */
export const buildDisplayNameToKeyMap = (allLabels: ConfigurationLabel[]): Map<string, string> => {
	const displayNameMap = new Map<string, string>();
	allLabels.forEach((label) => {
		const originalKey = label.label.toLowerCase();
		displayNameMap.set(originalKey, originalKey);
		if (label.displayName) {
			const displayNameLower = label.displayName.toLowerCase();
			displayNameMap.set(displayNameLower, originalKey);
		}
	});
	return displayNameMap;
};

/**
 * Build a map of original label keys to their display names
 */
export const buildKeyToDisplayNameMap = (allLabels: ConfigurationLabel[]): Map<string, string> => {
	const keyToDisplayNameMap = new Map<string, string>();
	allLabels.forEach((label) => {
		const originalKey = label.label.toLowerCase();
		if (label.displayName) {
			keyToDisplayNameMap.set(originalKey, label.displayName);
		}
	});
	return keyToDisplayNameMap;
};

/**
 * Normalize a label key by mapping display names back to original keys
 * @example "Unit ID" → "unitid" (if Unit ID is the display name for unitId)
 */
const normalizeLabelKey = (key: string, displayNameMap?: Map<string, string>): string => {
	const keyLower = key.toLowerCase();
	if (displayNameMap) {
		return displayNameMap.get(keyLower) || keyLower;
	}
	return keyLower;
};

/**
 * Extract the key prefix from a label string (part before ':')
 * Normalizes display names back to original keys if displayNameMap is provided
 * @example "accessibility:pass" → "accessibility"
 * @example "Unit ID:value" → "unitid" (if Unit ID is display name for unitId)
 */
export const extractLabelKey = (label: string, displayNameMap?: Map<string, string>): string => {
	const colonIndex = label.indexOf(':');
	if (colonIndex <= 0) {
		return '';
	}
	const key = label.substring(0, colonIndex).trim();
	return normalizeLabelKey(key, displayNameMap);
};

/**
 * Parse a label string into key and value parts
 * Normalizes display names back to original keys if displayNameMap is provided
 * @example "environment:production" → { key: "environment", value: "production" }
 * @example "Unit ID:value" → { key: "unitid", value: "value" } (if Unit ID is display name for unitId)
 * @returns null if label format is invalid
 */
export const parseLabel = (
	label: string,
	displayNameMap?: Map<string, string>
): { key: string; value: string } | null => {
	const colonIndex = label.indexOf(':');
	if (colonIndex <= 0) {
		return null;
	}

	const key = label.substring(0, colonIndex).trim();
	const value = label.substring(colonIndex + 1).trim();

	if (!key || !value) {
		return null;
	}

	// Normalize the key to original format
	const normalizedKey = normalizeLabelKey(key, displayNameMap);

	return { key: normalizedKey, value };
};

/**
 * Extract the value from a config value (handles both string and object formats)
 * Internal helper used by transformLabelValuesToOptions
 */
const extractConfigLabelValue = (value: unknown): string =>
	typeof value === 'string' ? value : (value as { value: string }).value;

/**
 * Check if a label key is a core label (unitId or environment)
 */
export const isCoreLabel = (labelKey: string): boolean => {
	const normalizedKey = labelKey.toLowerCase();
	return (
		normalizedKey === UNIT_ID_LABEL_KEY.toLowerCase() ||
		normalizedKey === ENVIRONMENT_LABEL_KEY.toLowerCase()
	);
};

/**
 * Transform label values to OptionType format
 * If selectedLabelKey is provided, only shows values from the matching label
 * If excludeValues is provided, filters out those values (used for core labels)
 */
export const transformLabelValuesToOptions = (
	allLabels: ConfigurationLabel[],
	selectedLabelKey?: string,
	excludeValues?: string[]
): OptionType[] => {
	const key = selectedLabelKey ? selectedLabelKey.toLowerCase() : '';
	const excludeSet = excludeValues ? new Set(excludeValues.map((v) => v.toLowerCase())) : undefined;

	return allLabels
		.filter((label) => {
			const labelKey = label.label.toLowerCase();
			const displayName = label.displayName?.toLowerCase();
			const searchKey = key.toLowerCase();

			return (
				!key ||
				labelKey === searchKey ||
				(displayName && displayName === searchKey) ||
				// Also check if the key we are looking for is already normalized and matches
				labelKey === searchKey.replace(/\s/g, '')
			);
		})
		.flatMap(({ values }) => {
			if (!values) {
				return [];
			}
			return values
				.map((value) => {
					const labelValue = extractConfigLabelValue(value);
					return { value: labelValue, label: labelValue };
				})
				.filter((option) => option.value && option.value.trim().length > 0)
				.filter((option) => !excludeSet || !excludeSet.has(option.value.toLowerCase()));
		});
};
